/**
 * Gemello client della SSoT `logic/constants/sindacati.ts`. Legge da
 * `window.__sindacati__` (popolato nel layout NJK via filtro `script_var`)
 * la lista canonica dei 6 sindacati Fenice e la espone come
 * `Record<idString, SindacatoFenice>`.
 *
 * La mappa e' cached per durata pagina: il bundle non viene ri-eseguito tra
 * navigazioni — eventuali cambi di SSoT richiedono reload.
 */

import type { SindacatoFenice } from '@/types/sindacati/sindacato-info';

declare global {
  interface Window {
    __sindacati__?: SindacatoFenice[];
  }
}

export interface SindacatoOption {
  value: string;
  label: string;
}

// Label "Liberi" usata per sindacato vuoto/null/id 1 (NESSUNO) / id non trovato.
// Coerente con backend (filtro repository tratta '1'/''/null come "Liberi").
const LIBERI_LABEL = 'Liberi';

let cached: Record<string, SindacatoFenice> | null = null;

export function getSindacatiMap(): Record<string, SindacatoFenice> {
  if (cached) return cached;
  const list = Array.isArray(window.__sindacati__) ? window.__sindacati__ : [];
  const map: Record<string, SindacatoFenice> = {};
  for (const s of list) map[String(s.id)] = s;
  cached = map;
  return map;
}

/**
 * Risolve la label UX di un sindacato a partire dal suo id canonico Fenice
 * (1..6) usando la SSoT esposta via `window.__sindacati__`.
 *
 * Regola:
 *   - input vuoto/null/'0'/'1' o id non trovato → "Liberi"
 *   - se `siglaCategoriaEdile` non vuota (id 2/3/4) → ritorna quella
 *     (es. FILCA/FILLEA/FENEAL — retro-compat byte-by-byte con UI legacy)
 *   - altrimenti (id 5/6 con sigla pura) → ritorna `sigla` (ALTRO/CGIL-UIL)
 */
export function getSindacatoLabel(v: unknown): string {
  if (v === null || v === undefined || v === '') return LIBERI_LABEL;
  const code = String(v).trim();
  if (!code || code === '0' || code === '1') return LIBERI_LABEL;
  const info = getSindacatiMap()[code];
  if (!info) return LIBERI_LABEL;
  return info.siglaCategoriaEdile || info.sigla;
}

/**
 * Costruisce le option per dropdown filtro sindacato a partire dalla SSoT.
 * Garantisce label UX-friendly: id 1 → "Liberi" (anziche' "NESSUNO" raw),
 * id 2/3/4 → siglaCategoriaEdile (FILCA/FILLEA/FENEAL), id 5/6 → sigla.
 */
export function buildSindacatiOptions(): SindacatoOption[] {
  const list = Array.isArray(window.__sindacati__) ? window.__sindacati__ : [];
  return list.map(s => ({
    value: String(s.id),
    label: s.id === 1 ? LIBERI_LABEL : (s.siglaCategoriaEdile || s.sigla),
  }));
}

export type { SindacatoFenice };
